import { useEffect, useMemo, useState } from "react";
import Alert from "antd/es/alert";
import Button from "antd/es/button";
import Form from "antd/es/form";
import Select from "antd/es/select";
import SendOutlined from "@ant-design/icons/SendOutlined";
import { RenderableSurveyData, SurveyData, SurveyLanguage } from "../types";
import FieldsContainer from "components/Fields";
import { Store } from "antd/es/form/interface";
import {
  getLocalizedText,
  hasBadRatingAnswer,
  SURVEY_LANGUAGE_OPTIONS,
  SURVEY_TEXTS,
} from "../utils";

interface SurveyFormProps {
  jsonData: RenderableSurveyData;
  handleFormSubmit: (values: any) => void;
  initialValues?: Store;
  isLoading: boolean;
  language: SurveyLanguage;
  onLanguageChange: (language: SurveyLanguage) => void;
  submitError?: string;
}

const getQuestionGroups = (
  groups: SurveyData.Group[],
  predicate: (question: SurveyData.SurveyQuestions) => boolean,
  clearGroupName = false
) => {
  return groups.reduce<SurveyData.Group[]>((result, group) => {
    const questions = group.questions.filter(predicate);

    if (questions.length === 0) {
      return result;
    }

    result.push({
      ...group,
      groupName: clearGroupName ? "" : group.groupName,
      groupLabel: clearGroupName ? "" : group.groupLabel,
      questions,
    });

    return result;
  }, []);
};

const SurverForm = ({
  jsonData,
  handleFormSubmit,
  initialValues,
  isLoading,
  language,
  onLanguageChange,
  submitError,
}: SurveyFormProps) => {
  const [form] = Form.useForm();
  const [formValues, setFormValues] = useState<Store>(initialValues || {});
  const locale = SURVEY_TEXTS[language];

  useEffect(() => {
    form.setFieldsValue(initialValues || {});
    setFormValues(initialValues || {});
  }, [form, initialValues]);

  const textWithLineBreaks = (text = "") => {
    return text
      .replace(/\s*¶\s*/g, "\n")
      .split(/\r?\n/)
      .map((line, index) => <div key={index}>{line}</div>);
  };

  const headerText = getLocalizedText(
    jsonData.surveyTemplate.headerText,
    language
  );
  const footerText = getLocalizedText(
    jsonData.surveyTemplate.footerText,
    language
  );
  const regularQuestionGroups = useMemo(
    () =>
      getQuestionGroups(
        jsonData.groups,
        (question) => !question.showIfBadRating
      ),
    [jsonData.groups]
  );
  const badRatingQuestionGroups = useMemo(
    () =>
      getQuestionGroups(
        jsonData.groups,
        (question) => !!question.showIfBadRating,
        true
      ),
    [jsonData.groups]
  );
  const hasBadRating = useMemo(
    () => hasBadRatingAnswer(jsonData.groups, formValues),
    [formValues, jsonData.groups]
  );

  return (
    <div className="surveyContent">
      <div className="surveyToolbar">
        <label className="surveyLanguageLabel" htmlFor="surveyLanguage">
          {locale.languageLabel}
        </label>
        <Select
          id="surveyLanguage"
          className="surveyLanguageSelect"
          options={SURVEY_LANGUAGE_OPTIONS}
          size="small"
          value={language}
          onChange={onLanguageChange}
        />
      </div>

      {headerText && (
        <div className="surveyIntro">{textWithLineBreaks(headerText)}</div>
      )}

      <Form
        form={form}
        className="surveyForm"
        onFinish={handleFormSubmit}
        onValuesChange={(_, allValues) => setFormValues(allValues)}
        initialValues={initialValues}
        noValidate
        labelWrap={true}
        labelAlign="left"
        colon={false}
      >
        <FieldsContainer
          fieldsData={regularQuestionGroups}
          language={language}
        />

        {hasBadRating && badRatingQuestionGroups.length > 0 && (
          <div className="surveyBadRatingQuestions">
            <FieldsContainer
              fieldsData={badRatingQuestionGroups}
              language={language}
              preserve={false}
            />
          </div>
        )}

        {submitError && (
          <Alert
            className="surveyAlert"
            type="error"
            showIcon
            message={submitError}
          />
        )}

        <div className="surveyActions">
          <Button
            type="primary"
            htmlType="submit"
            className="btnSend"
            loading={isLoading}
            icon={<SendOutlined />}
          >
            {locale.submit}
          </Button>
        </div>
      </Form>

      {footerText && (
        <div className="surveyFooter">{textWithLineBreaks(footerText)}</div>
      )}
    </div>
  );
};

export default SurverForm;
